import { Children, cloneElement, isValidElement } from 'react';

import { cn } from '../../lib/utils';
import { Panel } from './Panel';
import type { PanelColumn, PanelGridProps, PanelProps } from './types';

// The content canvas: full-bleed, two columns from xl, split by 1px hairlines.
// The canvas owns the surface colour so panels stay transparent — the design's
// per-panel fills disagree with each other.
//
// It also owns which column each panel lands in. Walking the children with a
// cursor is what lets a spanning panel exist at all: position alone stops
// matching the column as soon as one cell is wider than one column.
export function PanelGrid({ children, className }: PanelGridProps) {
  let cursor: 0 | 1 = 0;

  const placed = Children.map(children, (child) => {
    if (!isValidElement(child) || child.type !== Panel) return child;

    const span = (child.props as PanelProps).span ?? 1;
    let column: PanelColumn;

    if (span === 2) {
      // A spanner always starts a row, so the next panel starts one too.
      column = 'full';
      cursor = 0;
    } else {
      column = cursor === 0 ? 'left' : 'right';
      cursor = cursor === 0 ? 1 : 0;
    }

    return cloneElement(child as React.ReactElement<PanelProps>, { column });
  });

  return (
    <div
      className={cn(
        'grid grid-cols-1 bg-[var(--color-canvas-base)] xl:grid-cols-2',
        '[&>*]:border-t [&>*]:border-[var(--color-border-faint)]',
        className,
      )}
    >
      {placed}
    </div>
  );
}
